import React from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ProjectInterface } from "../../../../models/project.model";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { format } from "date-fns";
import { createProject } from "../../../../api/project";
// shadcn UI components
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "../../../../components/ui/card";
import { Switch } from "../../../../components/ui/switch";
import { Calendar } from "../../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/popover";
import { CalendarIcon, Loader2 } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../../components/ui/form";

// Validation schema
const projectSchema = yup.object({
    name: yup.string().required("Project name is required"),
    description: yup.string(),
    startDate: yup.date(),
    endDate: yup.date().nullable().optional(),
    imageUrl: yup.string(),
    slack_channel_id: yup.string(),
    url: yup.string(),
    private: yup.boolean(),
    status: yup.boolean(),
    clientId: yup.string(),
}).required();

const CreateProject: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [useFileUpload, setUseFileUpload] = React.useState(true);
    const [imageUrl, setImageUrl] = React.useState<string | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);

    // Form definition
    const form = useForm<Omit<ProjectInterface, "_id">>({
        resolver: yupResolver(projectSchema),
        defaultValues: {
            name: "",
            description: "",
            startDate: new Date(),
            endDate: null,
            imageUrl: "",
            slack_channel_id: "",
            private: false,
            status: true,
            url: "",
            clientId: "",
        },
    });

    const mutation = useMutation({
        mutationFn: (payload: Omit<ProjectInterface, "_id">) => createProject({ ...payload }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["projects"] });
            setIsLoading(false);
            form.reset();
            navigate("/projects"); // Redirect to projects list
        },
        onError: (error: any) => {
            setIsLoading(false);
            console.error("Error creating project:", error);
        },
    });

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files ? e.target.files[0] : null;
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                setImageUrl(result);
                form.setValue("imageUrl", result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageUrlChange = (url: string) => {
        setImageUrl(url);
        form.setValue("imageUrl", url);
    };

    const handleToggleImageInput = () => {
        setUseFileUpload((prev) => !prev);
        if (useFileUpload) {
            // Switching to URL input
            setImageUrl(form.getValues("imageUrl") || null);
        } else {
            // Switching to file upload
            setImageUrl(null);
        }
    };

    const onSubmit = (data: Omit<ProjectInterface, "_id">) => {
        setIsLoading(true);
        mutation.mutate({
            ...data,
            imageUrl: imageUrl || "",
        });
    };

    return (
        <div className="bg-gray-50 min-h-screen py-8 px-4">
            <Card className="max-w-3xl mx-auto">
                <CardHeader className="bg-gradient-to-r from-green-500 to-teal-600 text-white">
                    <CardTitle>Create New Project</CardTitle>
                    <CardDescription className="text-green-100">Enter your project details below</CardDescription>
                </CardHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <CardContent className="space-y-6 pt-6">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Project Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter project name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Textarea placeholder="Describe your project" rows={4} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="slack_channel_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Slack Channel ID</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter Slack Channel ID" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="url"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Link</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter Link" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="clientId"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Client</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter Client" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-2 gap-6">
                                <FormField
                                    control={form.control}
                                    name="status"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-col">
                                            <FormLabel>Status</FormLabel>
                                            <FormControl>
                                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="private"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-col">
                                            <FormLabel>Private</FormLabel>
                                            <FormControl>
                                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FormField
                                    control={form.control}
                                    name="startDate"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-col">
                                            <FormLabel>Start Date</FormLabel>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <FormControl>
                                                        <Button
                                                            variant="outline"
                                                            className={cn(
                                                                "pl-3 text-left font-normal",
                                                                !field.value && "text-muted-foreground"
                                                            )}
                                                        >
                                                            {field.value ? (
                                                                format(field.value, "PPP")
                                                            ) : (
                                                                <span>Pick a date</span>
                                                            )}
                                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                        </Button>
                                                    </FormControl>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0" align="start">
                                                    <Calendar
                                                        mode="single"
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        initialFocus
                                                        className="bg-white"
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="endDate"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-col">
                                            <FormLabel>End Date</FormLabel>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <FormControl>
                                                        <Button
                                                            variant="outline"
                                                            className={cn(
                                                                "pl-3 text-left font-normal",
                                                                !field.value && "text-muted-foreground"
                                                            )}
                                                        >
                                                            {field.value ? (
                                                                format(field.value, "PPP")
                                                            ) : (
                                                                <span>Pick a date</span>
                                                            )}
                                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                        </Button>
                                                    </FormControl>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0" align="start">
                                                    <Calendar
                                                        mode="single"
                                                        selected={field.value || undefined}
                                                        onSelect={field.onChange}
                                                        initialFocus
                                                        className="bg-white"
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="imageUrl"
                                render={({ field }) => (
                                    <FormItem>
                                        <div className="flex items-center justify-between">
                                            <FormLabel>Project Image</FormLabel>
                                            <div className="flex items-center space-x-2">
                                                <span className={cn("text-xs", !useFileUpload ? "text-green-600 font-medium" : "text-gray-500")}>
                                                    URL
                                                </span>
                                                <Switch
                                                    checked={useFileUpload}
                                                    onCheckedChange={handleToggleImageInput}
                                                />
                                                <span className={cn("text-xs", useFileUpload ? "text-green-600 font-medium" : "text-gray-500")}>
                                                    Upload
                                                </span>
                                            </div>
                                        </div>
                                        <FormControl>
                                            {useFileUpload ? (
                                                <Input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                    className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                                                />
                                            ) : (
                                                <Input
                                                    type="url"
                                                    placeholder="Enter image URL (https://...)"
                                                    value={field.value || ""}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        handleImageUrlChange(e.target.value);
                                                    }}
                                                />
                                            )}
                                        </FormControl>
                                        <FormMessage />
                                        {imageUrl && (
                                            <div className="mt-3">
                                                <div className="text-sm text-gray-500 mb-1">Preview:</div>
                                                <img
                                                    src={imageUrl}
                                                    alt="Project Preview"
                                                    className="w-32 h-32 object-cover rounded-md border border-gray-200 shadow-sm"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = "https://via.placeholder.com/150?text=Image+Error";
                                                        target.onerror = null; // Prevents looping
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </FormItem>
                                )}
                            />
                        </CardContent>

                        <CardFooter className="flex justify-end space-x-3 pt-6">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate("/projects")}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={mutation.isPending || isLoading}
                            >
                                {(mutation.isPending || isLoading) ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    "Create Project"
                                )}
                            </Button>
                        </CardFooter>
                    </form>
                </Form>
            </Card>
        </div>
    );
};

export default CreateProject;